'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useEffect, useCallback } from 'react';
import { CheckboxButton } from './Checkbox.parts.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
/**
 * @component
 * @param {Object} props - 체크박스 컴포넌트 프로퍼티
 * @param {boolean} [props.checked=false] - 체크박스의 체크 상태
 * @param {(event: ChangeEvent<HTMLInputElement>) => void} [props.onChange] - 체크박스 상태 변경 시 호출되는 콜백 함수
 * @param {boolean} [props.disabled=false] - 체크박스 비활성화 여부
 * @param {boolean} [props.isError=false] - 체크박스 에러 상태 여부
 * @param {'box' | 'circle' | 'sub'} [props.type='box'] - 체크박스 타입
 * @param {string} [props.className] - 추가 CSS 클래스
 *
 * @example
 * // 기본 사용법
 * <Checkbox checked={isChecked} onChange={handleChange} />
 *
 * // 비활성화된 체크박스
 * <Checkbox disabled />
 *
 * // 에러 상태의 체크박스
 * <Checkbox isError />
 *
 * // 커스텀 스타일이 적용된 체크박스
 * <Checkbox className="custom-checkbox" />
 *
 * // 체크박스 상태 변경 핸들링
 * const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
 *   console.log('체크박스 상태:', e.target.checked);
 * };
 * <Checkbox onChange={handleChange} />
 */ const Checkbox = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { checked = false, onChange, disabled = false, isError = false, type = 'box', className } = _param, rest = _object_without_properties(_param, [
        "checked",
        "onChange",
        "disabled",
        "isError",
        "type",
        "className"
    ]);
    const [isChecked, setIsChecked] = useState(checked);
    const [isFocused, setIsFocused] = useState(false);
    useEffect(()=>{
        setIsChecked(checked);
    }, [
        checked
    ]);
    const handleChange = useCallback((e)=>{
        if (disabled) return;
        if (onChange) {
            onChange(e);
            setIsChecked(e.target.checked);
            if (!e.target.checked) setIsFocused(false);
        }
    }, [
        onChange,
        disabled
    ]);
    const handleFocus = ()=>{
        setIsFocused(true);
    };
    const handleBlur = ()=>{
        setIsFocused(false);
    };
    return /*#__PURE__*/ jsxs("label", {
        "data-testid": "design-system-checkbox",
        className: "group flex size-fit",
        children: [
            /*#__PURE__*/ jsx("input", _object_spread({
                ref: ref,
                className: "absolute h-0 w-0 opacity-0",
                type: "checkbox",
                checked: isChecked,
                onChange: handleChange,
                onFocus: handleFocus,
                onBlur: handleBlur,
                disabled: disabled
            }, rest)),
            /*#__PURE__*/ jsx(CheckboxButton, {
                checked: isChecked,
                disabled: disabled,
                isError: isError,
                isFocused: isFocused,
                type: type,
                className: className
            })
        ]
    });
});
Checkbox.displayName = 'Checkbox';

export { Checkbox as default };
